/** Shared Excel column defs for entity list pages — pass `t` so labels follow UI language. */
import { formatBranchMode } from '../config/branchModes';

function statusLabel(t, active) {
    return active === false
        ? t('common.archived', { defaultValue: 'Archived' })
        : t('common.active', { defaultValue: 'Active' });
}

function sessionDaysLabel(t, row) {
    const raw = row.sessionDays ?? row.session_days ?? '';
    return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((x) => {
            const n = Number(x);
            if (Number.isInteger(n) && n >= 1 && n <= 7) {
                return t(`days.short.${n}`, { defaultValue: x });
            }
            return x;
        })
        .join(', ') || '';
}

export function getBranchListExportColumns(t) {
    return [
        { header: t('exports.branchId', { defaultValue: 'Branch ID' }), key: 'id', render: (v, r) => r.id ?? r.branchId ?? '', width: 10 },
        { header: t('exports.name', { defaultValue: 'Name' }), key: 'name', width: 24 },
        { header: t('exports.code', { defaultValue: 'Code' }), key: 'branchCode', width: 14 },
        { header: t('exports.phone', { defaultValue: 'Phone' }), key: 'phoneNumber', width: 16 },
        {
            header: t('exports.mode', { defaultValue: 'Mode' }),
            key: 'mode',
            render: (v) => (v ? formatBranchMode(v) : ''),
            width: 14,
        },
        {
            header: t('exports.manager', { defaultValue: 'Manager' }),
            key: 'managerName',
            render: (v) => v || '',
            width: 22,
        },
        {
            header: t('exports.status', { defaultValue: 'Status' }),
            key: 'active',
            render: (v) => statusLabel(t, v),
            width: 10,
        },
    ];
}

export function getLevelListExportColumns(t) {
    return [
        { header: t('exports.levelId', { defaultValue: 'Level ID' }), key: 'levelId', render: (v, r) => r.levelId ?? r.id ?? '', width: 10 },
        { header: t('exports.name', { defaultValue: 'Name' }), key: 'name', width: 24 },
        {
            header: t('exports.branch', { defaultValue: 'Branch' }),
            key: 'branchName',
            render: (v, r) => v || r.branch?.name || '',
            width: 22,
        },
        {
            header: t('exports.status', { defaultValue: 'Status' }),
            key: 'active',
            render: (v) => statusLabel(t, v),
            width: 10,
        },
    ];
}

export function getBatchListExportColumns(t) {
    return [
        { header: t('exports.batchId', { defaultValue: 'Batch ID' }), key: 'batchId', render: (v, r) => r.batchId ?? r.id ?? '', width: 10 },
        { header: t('exports.batchName', { defaultValue: 'Batch Name' }), key: 'name', width: 24 },
        { header: t('exports.level', { defaultValue: 'Level' }), key: 'levelName', width: 20 },
        {
            header: t('exports.branch', { defaultValue: 'Branch' }),
            key: 'branchName',
            render: (v, r) => v || r.branch?.name || r.branch?.branchName || '',
            width: 20,
        },
        {
            header: t('exports.sessionDays', { defaultValue: 'Session Days' }),
            key: 'sessionDays',
            render: (v, r) => sessionDaysLabel(t, r),
            width: 24,
        },
        {
            header: t('exports.status', { defaultValue: 'Status' }),
            key: 'active',
            render: (v) => statusLabel(t, v),
            width: 10,
        },
    ];
}

export function getDiscountListExportColumns(t) {
    return [
        { header: t('exports.id', { defaultValue: 'ID' }), key: 'id', render: (v, r) => r.id ?? r.discountId ?? '', width: 8 },
        { header: t('exports.code', { defaultValue: 'Code' }), key: 'code', width: 14 },
        { header: t('exports.name', { defaultValue: 'Name' }), key: 'name', width: 24 },
        {
            header: t('exports.value', { defaultValue: 'Value' }),
            key: 'discountValue',
            render: (v, r) => {
                const val = r.discountValue ?? v;
                if (val == null) return '';
                return r.discountType === 'PERCENT' ? `${val}%` : String(val);
            },
            width: 12,
        },
        {
            header: t('exports.scope', { defaultValue: 'Scope' }),
            key: '_scope',
            render: (_, r) => {
                if (r.batchId) return t('exports.scopeBatch', { id: r.batchId, defaultValue: 'Batch #{{id}}' });
                if (r.levelId) return t('exports.scopeLevel', { id: r.levelId, defaultValue: 'Level #{{id}}' });
                if (r.branchId) return t('exports.scopeBranch', { id: r.branchId, defaultValue: 'Branch #{{id}}' });
                return t('exports.scopeGlobal', { defaultValue: 'Global' });
            },
            width: 16,
        },
        {
            header: t('exports.status', { defaultValue: 'Status' }),
            key: 'isActive',
            render: (v) => statusLabel(t, v),
            width: 10,
        },
    ];
}

/** @deprecated Prefer get*ListExportColumns(t) */
export const BRANCH_LIST_EXPORT_COLUMNS = getBranchListExportColumns((k, o) => o?.defaultValue || k);
export const LEVEL_LIST_EXPORT_COLUMNS = getLevelListExportColumns((k, o) => o?.defaultValue || k);
export const BATCH_LIST_EXPORT_COLUMNS = getBatchListExportColumns((k, o) => o?.defaultValue || k);
export const DISCOUNT_LIST_EXPORT_COLUMNS = getDiscountListExportColumns((k, o) => o?.defaultValue || k);
